/**
 * Pharmacy data access.
 *
 * Thin on purpose: every figure is computed by the API — batches, on-hand,
 * costs, balances, what is saleable. The screens format it. A pharmacy query
 * key always starts with 'pharmacy', so one invalidation after any pharmacy
 * write refreshes every pharmacy screen, and nothing outside them.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';

export const PHARMACY_KEY = 'pharmacy';

/** GET /pharmacy/<path>, with query params. */
export function usePharmacy<T>(
  path: string,
  params: Record<string, string | number | boolean | undefined | null> = {},
  enabled = true,
): UseQueryResult<T> {
  const base = useBusinessPath();
  return useQuery({
    queryKey: [PHARMACY_KEY, base(''), path, params],
    queryFn: () => api.get<T>(base(`/pharmacy${path}${qs(params)}`)),
    enabled,
    placeholderData: (previous) => previous,
  });
}

/** A pharmacy write. Refreshes every pharmacy screen when it succeeds. */
export function usePharmacyWrite<TBody = unknown, TResult = unknown>(
  method: 'post' | 'patch' | 'put' | 'del',
  path: string | ((body: TBody) => string),
) {
  const base = useBusinessPath();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => {
      const p = typeof path === 'function' ? path(body) : path;
      const url = base(`/pharmacy${p}`);
      return method === 'del' ? api.del<TResult>(url, body) : api[method]<TResult>(url, body);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [PHARMACY_KEY] });
      void client.invalidateQueries({ queryKey: ['sales'] });
    },
  });
}

/** Multipart upload under /pharmacy (prescription documents, imports). */
export function usePharmacyUpload<TResult = unknown>(path: string | ((id: string) => string)) {
  const base = useBusinessPath();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, form }: { id?: string; form: FormData }) =>
      api.upload<TResult>(base(`/pharmacy${typeof path === 'function' ? path(id ?? '') : path}`), form),
    onSuccess: () => void client.invalidateQueries({ queryKey: [PHARMACY_KEY] }),
  });
}

/** A fresh idempotency key: one per user action, reused on retry of that action. */
export function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ── Response shapes used by more than one screen ───────────────────────────

export interface UnitView {
  id: string;
  name: string;
  baseQuantity: number;
  barcode: string | null;
  price: string | null;
  isBase: boolean;
  sellable: boolean;
  purchasable: boolean;
}

export interface MedicineListItem {
  id: string;
  name: string;
  genericName: string | null;
  strength: string | null;
  dosageForm: string | null;
  rxClass: string;
  isMedicine: boolean;
  tracksBatches: boolean;
  baseUnitName: string;
  onHand: number;
  saleableOnHand: number | null;
  nearestExpiry: string | null;
  /** room, cool, refrigerated or frozen. */
  storageRequirement?: string;
  units: UnitView[];
}

export interface BatchRow {
  batchId: string;
  productId: string;
  productName: string | null;
  batchNumber: string;
  expiryDate: string;
  receivedOn: string;
  unitCost: string;
  branchId: string;
  onHand: number;
  held: boolean;
  holdKinds: string[];
  expired: boolean;
  nearExpiry: boolean;
  value: string;
  /** As printed on the pack, when entered. */
  manufacturedOn?: string | null;
  /** Held only because a delivery is waiting for a pharmacist's check. */
  awaitingCheck?: boolean;
}

export interface Valuation {
  currencyCode: string;
  onShelf: string;
  saleable: string;
  quarantinedOrRecalled: string;
  expired: string;
  inTransit: string;
  total: string;
  /** Stock not tracked by batch, at what it cost (average paid, else catalogue cost); inside onShelf and saleable. */
  notBatchTracked?: string;
  /** Items on hand with no cost known at all: counted, not valued. */
  uncostedItems: number;
  /** Counted stock the owner included in inventory value (0169): never on hand, never saleable. */
  awaitingVerification?: { label: string; products: number; units: number; value: string } | null;
  /** Total plus what awaits batch/expiry verification; null until the owner includes it. */
  totalIncludingAwaiting?: string | null;
  /** Units held: on hand, counted stock pending its batches, and both. */
  units?: { onHand: number; countedPending: number; recorded: number };
}

export interface Overview {
  alerts: { expired: number; nearExpiry: number; held: number; lowStock: number; pendingAdjustments: number };
  work: Record<string, number>;
  valuation: Valuation;
}

export interface PharmacySettings {
  expiryAlertDays: number;
  sellCutoffDays: number;
  maxDiscountPercent: string;
  enforcePrescriptions: boolean;
  requireRegisteredReviewer: boolean;
  restrictedRequiresWitness: boolean;
  onlineOrdersEnabled: boolean;
  deliveryEnabled: boolean;
  refillRemindersEnabled: boolean;
  companyBillingEnabled: boolean;
  cardIntegrationEnabled: boolean;
  restrictedSalesEnabled: boolean;
  restrictedSalesSignOff: string | null;
  restrictedSalesEnabledAt: string | null;
  loyaltyEarningEnabled: boolean;
  loyaltyRedemptionEnabled: boolean;
  loyaltySpendPerPoint: string;
  loyaltyPointValue: string;
  dependencies: Record<string, string>;
}

export interface CustomerLoyalty {
  customerId: string;
  balance: number;
  balanceValue: string;
  pointValue: string;
  earningEnabled: boolean;
  redemptionEnabled: boolean;
  entries: Array<{
    id: string;
    kind: 'earn' | 'earn_reversal' | 'redeem' | 'redeem_restore';
    points: number;
    rate: string;
    value: string;
    note: string | null;
    at: string;
    saleId: string | null;
    saleReference: string | null;
  }>;
}

export interface LoyaltySummary {
  pointsEarned: number;
  pointsReversed: number;
  pointsRedeemed: number;
  pointsRestored: number;
  discountGiven: string;
  pointsOutstanding: number;
  outstandingValue: string;
  pointValue: string;
  customersWithPoints: number;
  customersBelowZero: number;
}

export interface ListOf<T> {
  data: T[];
}
